'use strict';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '@core/dialog/dialog.component';
import * as GC from '@grapecity/spread-sheets';
import { clipboardClearOnCut } from '@helpers/clipboard-events';
import { convertBlankTo } from '@helpers/convertBlankTo';
import { bindSpreadJSEventsToSheet, mapActionKeys } from '@helpers/spread/map-action-keys';
import { resumeAll } from '@helpers/spread/resume-all';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { checkDropdownValues } from '@helpers/spread/sheet-functions';
import { suspendAll } from '@helpers/spread/suspend-all';
import { DialogSaveObject } from '@models/dialog-save-object';
import { HoverNotification } from '@models/hover-notification';
import { LiquiditySource } from '@models/liquidity-source';
import { PermissionsService } from '@services/auth/permissions.service';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import { CanDeactivateGuard } from '@services/guards/can-deactivate/can-deactivate-guard.service';
import { LiquiditySourcesService } from '@services/liquidity-sources/liquidity-sources.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { backoff } from '@shared/helpers/backoff';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { Toast } from 'ngx-toastr';
import { cloneDeep, isEqual } from 'lodash-es';
import { BehaviorSubject, forkJoin, fromEvent, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as SPREAD_CONFIG_LIQSRC from './liquidity-sources.spread.config';
import { ToastLevel } from '@shared/toasterSetup';

const SPREADCONFIG = SPREAD_CONFIG_LIQSRC.SpreadConfig;
const ACCEPTED_TYPES = SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES;
const MINIMUM_ROWS = SPREAD_CONFIG_LIQSRC.SpreadConfig.MINIMUM_ROWS;

const QTY_EMPTY_BOTTOM_ROWS = 2;
const BLANK_ROWS_AT_BOTTOM = (() => {
  const result = [];
  for (let i = 0; i < QTY_EMPTY_BOTTOM_ROWS; i++) {
    // For each time you add the line, is created a new row in the end of the spreadsheet
    const defaultRow = {};
    result.push(defaultRow);
  }
  return result;
})();

const COLS_INDEX = {
  name: 0,
  mnemonic: 1,
  type: 2,
  calculate_group: 3,
  draw_order: 4,
  balance_sheet_account_mnemonic: 5,
  balance_sheet_add_factor: 6,
};

@Component({
  selector: 'app-liquidity-sources',
  templateUrl: './liquidity-sources.component.html',
  styleUrls: ['./liquidity-sources.component.scss'],
})
export class LiquiditySourcesComponent implements OnInit, AfterViewInit, OnDestroy, CanDeactivateGuard {
  /*
  =================
  Properties
  =================
  */
  private destroyed$ = new Subject();
  // Hover Messages & Invalidations
  private hasInvalidations = false;
  public invalidMessages: string[] = [];
  public invalidRows: number[] = [];
  public hoverMessage = '';
  public hoverTitle = '';
  public hoverValue = '';
  public notification: HoverNotification = {};
  public mouseEvent: any;
  // Spread
  public readonly spreadConfig = SPREADCONFIG;

  public data: LiquiditySource[] = [];
  public dataOriginal: LiquiditySource[] = null;
  public liquiditySources: LiquiditySource[] = [];

  private spread: GC.Spread.Sheets.Workbook = null;
  private sheet: GC.Spread.Sheets.Worksheet = null;
  public isEdit = false;
  public showLatest = false;
  public isSaving = false;
  public isSaved = false;
  private willCauseDataLoss = false;
  public failSaving = false;
  public isLoading = true;
  public validSheet = false;
  private validating = false;
  public sheetArea = GC.Spread.Sheets.SheetArea.viewport;
  public isSheetValid$ = new BehaviorSubject(false);
  @ViewChild('spreadContainer', { static: false }) container: ElementRef;

  // Users
  public editingUsers = <any>[];
  public editingUsersList = '';
  private userRoleCanEdit: boolean;
  private reverting: boolean;

  private _saveEnabled = false;

  public saveEnabled = async (): Promise<boolean> => {
    if (this.userRoleCanEdit) {
      const newCondition = this.validSheet && !this.isSaving && !this.isLoading;

      if (newCondition !== this._saveEnabled) {
        this._saveEnabled = newCondition;
        // ! warning
        // -- Removing the next line introduces the defect of canDeactivate pop-up being shown on first load

        this.menu.selectedMenuItemCanDeactivate(!newCondition);
      }
      return newCondition;
    }
  };
  balanceSheetAccounts: string[];

  /*
      =========================
        Constructor &
        event-cycle methods
      =========================
  */
  constructor(
    private liqSrcService: LiquiditySourcesService,
    private notifications: NotificationsService,
    private zone: NgZone,
    public menu: MenuService,
    public dialogRef: MatDialogRef<DialogComponent>,
    private permissions: PermissionsService,
    private balanceSheetService: BalanceSheetService
  ) {}

  ngOnInit() {
    this.menu.isSidePanelVisible = false;
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureLiquiditySources') === 'W';

    this.loadData();
  }

  ngAfterViewInit(): void {
    this.initSubscriptions();
  }

  private initSubscriptions() {
    // Mouse Move over spreadsheet container - available in both edit and read-only modes
    fromEvent(this.container.nativeElement, 'mousemove')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: MouseEvent) => this.onMouseMove(event));

    // Dialog Save Button changes (listenToDialogSaveButton)
    this.menu.dialogSaveButton$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(50),
        distinctUntilChanged(),
        backoff(3, 250),
        catchError(err => of([]))
      )
      .subscribe((val: DialogSaveObject) => {
        // Only respond to click event if was triggered for this page
        if (val.page === 'liquidity-sources') {
          // Discard lost-of-data changes functionality
          if (val.source === `${val.page}-discard`) {
            this.menu.dialogSaveButton = { ...this.menu.dialogSaveButton, source: `${val.page}-discarded` };
            this.discardChanges();
          }
          if (val.clicked && val.enabled && this.userRoleCanEdit) {
            this.saveData().then(() => {
              this.isValidSheet();
            });
          }
        }
      });

    // DeActivate Popup close
    this.menu.deactivatePopupClosed$.pipe(takeUntil(this.destroyed$)).subscribe(popupResult => {
      if (popupResult === false) {
        this.spread.focus();
      }
    });

    // Only set up edit-related subscriptions if user has edit permissions
    if (this.userRoleCanEdit) {
      // Helper for edit actions
      const editActionsCallback = () => {
        this.isEdit = true;
        this.onEditedSpreadSheet();
        this.isValidSheet();
      };

      // Right Click paste and ctrl+v
      fromEvent(document.getElementById(spreadsheetConfig.containerParentId), 'keyup')
        .pipe(takeUntil(this.destroyed$))
        .subscribe((event: KeyboardEvent) => {
          const rightClickPasted = event && ['KeyV'].includes(event.code) && event.ctrlKey; // It emits as Ctrl+V
          if (rightClickPasted) {
            editActionsCallback();
            this.updateSaveButton('ClipboardPasted');
          }
        });

      // Key Up over spreadsheet container
      fromEvent(this.container.nativeElement, 'keyup')
        .pipe(takeUntil(this.destroyed$))
        .subscribe((event: KeyboardEvent) => {
          const deleted = event && ['Delete', 'Backspace'].includes(event.code);
          if (deleted) {
            editActionsCallback();
            this.updateSaveButton('Deleted');
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.notifications.hideToasts();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /*
      ========================
        SpreadSheet-related
              methods
      ========================
  */

  /**
   * Initializes the SpreadSheet setup
   */
  public workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  /**
   * Setup and customize the GC SpreadSheet
   *
   */
  private setupWorkBook() {
    this.zone.run(() => {
      // Set up spreadsheet
      this.sheet = this.spread.getActiveSheet();

      if (this.sheet) {
        this.sheet.suspendPaint();

        // =========================
        //   General Sheet Options
        // =========================
        this.sheet.name('Liquidity Sources');

        setDefaultBookOptions(this.spread.options, {
          // Overwrite options here
          allowExtendPasteRange: true,
          allowUndo: this.userRoleCanEdit, // Only allow undo for users with edit permissions
        });
        setDefaultSheetOptions(this.sheet.options);

        // Set protection options based on user permissions
        if (this.userRoleCanEdit) {
          // Protection options (after setDefaultSheetOptions)
          this.sheet.options.protectionOptions = {
            // Lock cell options
            allowSelectLockedCells: false,
            allowSelectUnlockedCells: true,
          };
          this.sheet.options.isProtected = false;

          // Context Menu (right-click) - full menu for users with edit permissions
          SpreadContextMenu.setCopyCutPaste(this.spread)
            .enableInsertRowsAboveAndBelow(this.spread)
            .enableDeleteRows(this.spread);
        } else {
          // Read-only mode
          this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
          this.sheet.options.isProtected = true;
          this.sheet.options.protectionOptions.allowSelectLockedCells = true;
          // Only allow copy/paste in read-only mode
          SpreadContextMenu.setCopyCutPaste(this.spread);
        }

        this.onEditedSpreadSheet();

        // On cell edit leave, on cell value change, on paste
        this.setupWorkBookEventBindings();

        this.isSaved = false;
        this.isEdit = false;
        this.sheet.resumePaint();
        this.spread.focus();
      }
    });
  }

  /**
   * Event listeners for the spreadsheet
   */
  private setupWorkBookEventBindings() {
    // Only map action keys if user has edit permissions
    if (this.userRoleCanEdit) {
      mapActionKeys(this.spread, this.sheet, null, this.userRoleCanEdit);
    }
    bindSpreadJSEventsToSheet(this.sheet);

    // Set up ValueChanged event for both edit and read-only modes
    this.sheet.bind(GC.Spread.Sheets.Events.ValueChanged, (event, info) => {
      // In read-only mode, prevent value changes and restore original value
      if (!this.userRoleCanEdit) {
        if(!this.reverting) {
          this.reverting = true;
          this.sheet.setValue(info.row, info.col, info.oldValue);
          this.reverting = false;
        }
        
        return;
      }
      
      // In edit mode, process the value change
      this.valueChangedCallback(event, info);
    });

    // Only set up clipboard events if user has edit permissions
    if (this.userRoleCanEdit) {
      this.setClipboardPastingEvent();

      // Trigger on cell change (for validation purposes)
      this.sheet.bind(GC.Spread.Sheets.Events.CellChanged, this.cellChangedCallback);

      // Trigger on pasted data
      this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (event, info) => {
        this.isEdit = true; // Do not include this line inside onEditedSpreadSheet()
        this.onEditedSpreadSheet();
        this.isValidSheet();
        this.updateSaveButton('ClipboardPasted');

        // TODO: Move into parseRowForLiquiditySources()
        this.data.forEach((source: LiquiditySource) => {
          if (!source.hasOwnProperty('source_sid')) {
            source.source_sid = 0;
          }
        });
        
        // Force reprocessing of pasted data to ensure proper source_sid assignment
        
        this.forceReprocessPastedData();
      });

      // Trigger on drag fill
      this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (event, info) => {
        this.isEdit = true;
        this.onEditedSpreadSheet();
        this.isValidSheet();
        this.updateSaveButton('DragFillBlockCompleted');
        this.checkDataLoss();
      });

      // Trigger on drag and drop values
      this.sheet.bind(GC.Spread.Sheets.Events.DragDropBlockCompleted, (event, info) => {
        this.isEdit = true;
        this.onEditedSpreadSheet();
        this.isValidSheet();
        this.updateSaveButton('DragDropBlockCompleted');
        this.checkDataLoss();
      });

      // Trigger on row changes (insert/delete)
      this.sheet.bind(GC.Spread.Sheets.Events.RowChanged, (event, info) => {
        this.isEdit = true;
        this.onEditedSpreadSheet();
        this.isValidSheet();
        this.updateSaveButton(info.propertyName);
        this.checkDataLoss();
      });

      // Add delete key binding
      this.addDeleteKeyBinding();
      // Add undo/redo key bindings
      this.addUndoKeyBinding();
      this.addRedoKeyBinding();
    } else {
      // In read-only mode, prevent drag and drop operations
      this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (event, info) => {
        event.cancel = true;
      });
      this.sheet.bind(GC.Spread.Sheets.Events.DragDropBlockCompleted, (event, info) => {
        event.cancel = true;
      });
      this.sheet.bind(GC.Spread.Sheets.Events.RowChanged, (event, info) => {
        event.cancel = true;
      });
    }

    // These events should be available in both edit and read-only modes
    this.sheet.bind(GC.Spread.Sheets.Events.ValidationError, (e, info) => {
      // On Validation Error disable save button
      this.hasInvalidations = true;
    });
  }

  private setClipboardPastingEvent() {
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (event, info) => {
      // Prevent pasting in read-only mode
      if (!this.userRoleCanEdit) {
        event.cancel = true;
        return;
      }

      SheetFunctions.preventPastingColumnsBeyond(this.sheet, event, info);
      SheetFunctions.allowPastingRowsBeyond(this.sheet, info);

      // if is a cut event, it clears the clipboard content once the event ends
      clipboardClearOnCut(info, this.sheet);
    });
  }

  private cellChangedCallback = (event, info) => {
    // Filter events to only validate when value is modified
    if (info.propertyName === 'value') {
      checkDropdownValues(this.sheet, info.row, info.col);
      // Note: ValueChanged event handles the actual value change processing
    }
  };

  private valueChangedCallback(e, info) {
    // Do not include the next line inside onEditedSpreadSheet()
    this.isEdit = true;
    this.onEditedSpreadSheet();

    if (e.type === 'ValueChanged' && info.newValue === null) {
      setTimeout(() => {
        suspendAll(this.sheet);
        this.addValidatorsToRow(info.row);

        resumeAll(this.sheet);
        this.isValidSheet();
        this.updateSaveButton('ValueChanged / Edit --> delete');
        this.checkDataLoss();
      });
    } else {
      this.isValidSheet();
      this.updateSaveButton('ValueChanged / Edit');
    }
  }

  // Delete key binding workaround
  private addDeleteKeyBinding() {
    const actionOnDeletion = () => {
      const activeSheet = this.spread.getActiveSheet();
      const selectionRange = activeSheet.getSelections()[0];
      // ? Use all the selections array

      // Call the callback on value changed after the end of the event (to avoid unnecessary calls)
      const paramsObject = {
        sheet: activeSheet,
        sheetName: activeSheet.name(),
        row: selectionRange.row + selectionRange.rowCount - 1,
        col: selectionRange.col + selectionRange.colCount - 1,
        newValue: null,
      };
      paramsObject['oldValue'] = this.sheet.getCell(paramsObject.row, paramsObject.col).value();
      this.valueChangedCallback({ type: 'ValueChanged' }, paramsObject);
    };

    const deleteCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          // Undo feature for 'Delete' key only
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          actionOnDeletion();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };

    const commandManager = this.spread.commandManager();
    if (this.userRoleCanEdit) {
      commandManager.register('deleteKey', deleteCommand, GC.Spread.Commands.Key.del);
    }
  }

  private addUndoKeyBinding() {
    const actionOnUndo = () => {
      const activeSheet = this.spread.getActiveSheet();

      // Call the callback on value changed after the end of the event (to avoid unnecessary calls)
      const paramsObject = {
        sheet: activeSheet,
        sheetName: activeSheet.name(),
      };

      // Timeout needed to delay execution until the event ends
      setTimeout(() => this.valueChangedCallback({ type: 'Undo' }, paramsObject));
    };

    const undoCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          actionOnUndo();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };

    const commandManager = this.spread.commandManager();

    // Ctrl + Z
    commandManager.register('undo', undoCommand, GC.Spread.Commands.Key.z, true, false, false, false);
  }

  private addRedoKeyBinding() {
    const actionOnRedo = () => {
      const activeSheet = this.spread.getActiveSheet();
      // Call the callback on value changed after the end of the event (to avoid unnecessary calls)
      const paramsObject = {
        sheet: activeSheet,
        sheetName: activeSheet.name(),
      };

      // Timeout needed to delay execution until the event ends
      this.valueChangedCallback({ type: 'Redo' }, paramsObject);
    };

    const redoCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          // Undo feature for 'Delete' key only
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          actionOnRedo();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };

    const commandManager = this.spread.commandManager();
    // Ctrl + Y
    commandManager.register('redo', redoCommand, GC.Spread.Commands.Key.y, true, false, false, false);
  }

  private addEmptyRowsAfterValidation() {
    // 2. Add a new empty row after last default row has been validated
    const lastIndex = this.data.length - 1;

    const secondToLastRowValid = this.validateRow(lastIndex - 1);
    const lastRowValid = this.validateRow(lastIndex);

    if (lastRowValid || secondToLastRowValid) {
      this.zone.run(() => {
        const numberOfRows = lastRowValid
          ? QTY_EMPTY_BOTTOM_ROWS
          : secondToLastRowValid
          ? QTY_EMPTY_BOTTOM_ROWS - 1
          : QTY_EMPTY_BOTTOM_ROWS;
        this.addRowsAtTheEnd(numberOfRows);
      });
    }
  }

  /**
   * It is triggered when some edition of the spreadsheet happens.
   * It purges the values of the fields and calls the content validity checker
   */
  private onEditedSpreadSheet() {
    try {
      if (this.sheet) {
        // Zone run notifies Angular about changes, forcing the Changes Detection
        this.zone.run(() => {
          this.sheet.suspendPaint();
          this.isSaved = false;

          // Parse data
          
          this.data.forEach((row, index) => {
            const originalRow = { ...row };
            this.data[index] = this.liqSrcService.parseRowForLiquiditySources(row);            
          });
          
          if (this.isLoading) {
            this.setDataBinding();
          }

          if (this.isEdit) {
            // After one of the default rows of the bottom has been modified,
            // add another one to make sure they are two empty rows at the end
            this.addEmptyRowsAfterValidation();

            // ! Timeout needed -- why?
            // -- It delays execution until the parent spreadJS event ends (moment in which this.data is updated)
            setTimeout(() => {
              if (this.data.length === 0) {
                this.addRowsAtTheEnd();
              }
            });
          }

          if (isEqual(this.data, BLANK_ROWS_AT_BOTTOM)) {
            this.updateSaveButton('onEditedSpreadSheet with blank spreadsheet');
          }

          this.setValidations();
          this.sheet.resumePaint();
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Updates the data of the spreadsheet
   */
  private setDataBinding() {
    try {
      this.zone.run(() => {
        if (this.sheet) {
          this.sheet.autoGenerateColumns = true;
          this.sheet.setDataSource(this.data, true);
          this.sheet.bindColumns(SPREADCONFIG.columnsConfig);

          const rowCount = this.sheet.getRowCount();
          const colCount = this.sheet.getColumnCount();

          this.sheet.setColumnCount(colCount);
          this.sheet.setRowCount(rowCount || this.sheet.getDataItem.length);
          this.sheet.defaults.rowHeight = SPREADCONFIG.DEFAULT_VALUES.rowHeight;
          this.sheet.defaults.colWidth = SPREADCONFIG.DEFAULT_VALUES.colWidth;
        }
      });
    } catch (error) {
      // console.warn(`Error setting data binding`, { error });
    }
  }

  /*
      =================
          Validations
      =================
  */

  private isEmptyRow(rowIndex: number): boolean {
    const row = this.data[rowIndex];
    return (
      !row.name &&
      !row.mnemonic &&
      !row.type &&
      !row.calculate_group &&
      !row.draw_order &&
      !row.balance_sheet_account_mnemonic &&
      !row.balance_sheet_add_factor
    );
  }

  private validateRow(r: number) {
    if (!this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[r])) {
      const isEmpty = this.isEmptyRow(r);
      const validName = this.sheet.isValid(r, COLS_INDEX['name'], this.data[r]['name']);
      const validMnemonic = this.sheet.isValid(r, COLS_INDEX['mnemonic'], this.data[r]['mnemonic']);
      const validType = this.sheet.isValid(r, COLS_INDEX['type'], this.data[r]['type']);
      const validCalculateGroup = this.sheet.isValid(r, COLS_INDEX['calculate_group'], this.data[r]['calculate_group']);
      const validDrawOrder = this.sheet.isValid(r, COLS_INDEX['draw_order'], this.data[r]['draw_order']);
      const validBalanceSheetMnemonic = this.sheet.isValid(
        r,
        COLS_INDEX.balance_sheet_account_mnemonic,
        this.data[r].balance_sheet_account_mnemonic
      );
      const validAddFactor = this.sheet.isValid(r, COLS_INDEX.balance_sheet_add_factor, this.data[r].balance_sheet_add_factor);

      return (
        isEmpty ||
        (validName && validMnemonic && validType && validCalculateGroup && validDrawOrder && validAddFactor && validBalanceSheetMnemonic)
      );
    }
    return true;
  }

  private updateInvalidRows(isValid: boolean, rowNum: number) {
    this.zone.run(() => {
      this.invalidRows = isValid ? cloneDeep(this.invalidRows).filter(x => x !== rowNum + 1) : cloneDeep([...this.invalidRows, rowNum + 1]);
    });
  }

  public onMouseMove(event) {
    const { layerX: x, layerY: y } = event;
    // const x = layerX;
    // const y = layerY;

    const target = this.sheet.hitTest(x, y);

    if (target) {
      const { row, col } = target;
      // "IF" clause to avoid showing error messages on non-content areas of the spreadsheet

      if (row > -1 && col > -1) {
        const thisCell = this.sheet.getCell(row, col);
        const value = thisCell.value();
        const validator = thisCell.validator();
        const isValid = this.sheet.isValid(row, col, value);
        this.notification = {};
        const columnsWithValidations = [0, 1, 2, 3, 4, 5, 6];

        const validationCol = col > -1 ? col : -1;
        const validationMessages = [
          {
            field: 'Name',
            message: value ? `The Name cannot include wildcard characters as: ?, *, ~` : `Name cannot be empty`,
          },
          {
            field: 'Mnemonic',
            message: value
              ? // Don't allow the user to use liq src types values as mnemonics (case-insensitive)
                [...ACCEPTED_TYPES].map(type => `${type}`.toLowerCase()).includes(`${value}`.toLowerCase())
                ? `${value} cannot be used as mnemonic`
                : `The Mnemonic should be unique and should contain only numbers, letters, ''_'`
              : `Mnemonic cannot be empty`,
          },
          {
            field: 'Type',
            message: `No value has been selected for Type`,
          },
          {
            field: 'Calculate Group',
            message: `Calculate Group should be numeric and positive`,
          },
          {
            field: 'Draw Order',
            message: `Draw Order should be unique, numeric and positive`,
          },
          {
            field: 'Bal Sheet Account',
            message: `Balance Sheet Acct invalid. Account Mnemonic must exist in the balance sheet chart of accounts.`,
          },
          {
            field: 'Bal Sheet Add Factor',
            message: `Add Factor invalid. It needs to be a decimal number between 0 and 1000.`,
          },
        ];

        const cell = validationMessages[validationCol];

        if (validator && cell && !isValid) {
          if (columnsWithValidations.includes(col)) {
            this.notification = {
              title: `Invalid ${cell.field}`,
              value: value,
              body: `${cell.message}`,
              type: 'error',
            };
          }
        }
      }
    }
  }

  /**
   * Resets the values of some invalidation properties
   */
  private resetInvalidations() {
    // this.invalidMessage = null;
    this.zone.run(() => {
      this.invalidMessages = [];
      this.invalidRows = [];
      this.hasInvalidations = false;
      this.validSheet = false;
    });
  }

  private showSuccessToast() {
    this.notifications.hideToasts();
    const toasts = this.notifications.numberOfActiveToasts;

    // Not showing several toasts when there are several simultaneous saved changes (i.e. add + update + delete).
    if (toasts === 0) {
      const pageTitle = 'Liquidity Sources';
      this.notifications.showToastWithButtons(
        ToastLevel.Success,
        'Success',
        `${pageTitle} successfully updated!`,
        [
          {
            name: 'View Period Equations',
            class: 'btn_normal btn_outline_white btn-toast-action',
            action: () => {
              this.menu.navigateTo('liquidity-equations');
            }
          }
        ]
      );
    }
  }

  private showFailToast(body = `Error updating Liquidity Sources!`) {
    this.notifications.hideToasts();
    const toasts = this.notifications.numberOfActiveToasts;

    // Not showing several toasts when there are several simultaneous saved changes (i.e. add + update + delete).
    if (toasts === 0) {
      this.notifications.showToast(ToastLevel.Error, {message: body } as Toast);
    }
  }

  private showDiscardToast() {
    const body = `You have made changes that may cause data to be lost. Press 'Save' to confirm, or 'Discard'. `;
    const discardToast: Toast = {
      title: '',
      message:body
    } as Toast;
    this.notifications.hideToasts();
    this.notifications.showToast( ToastLevel.Warning,discardToast, 'Discard', 'liquidity-sources-discard');
  }

  private discardChanges() {
    this.sheet.reset();
    this.notifications.hideToasts();
    this.loadData();
  }

  private checkDataLoss() {
    this.liqSrcService.updateDeletions(this.data, this.dataOriginal);
    this.willCauseDataLoss = this.liqSrcService.setOfSourcesToDelete.size > 0;
    if (this.willCauseDataLoss) {
      this.showDiscardToast();
    }
  }

  /**
   * Checks the validity of all the sheet
   */

  private isValidSheet(): boolean {
    // Executed one time for each row
    try {
      this.resetInvalidations();

      // Each invalid row index will be pushed into 'invalidRows' array
      this.data.forEach((acc, rowIndex) => {
        if (!this.isEmptyRow(rowIndex)) {
          const isValid = this.validateRow(rowIndex);
          // So ngOnChange gets triggered and invalidRows update is detected
          this.updateInvalidRows(isValid, rowIndex);
        }
        this.invalidationsCheck(rowIndex);
      });
      if (this.data.length === QTY_EMPTY_BOTTOM_ROWS) {
        const nonDefaultRows = this.data.filter(row => !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(row));
        // Allow to save a blank spreadsheet
        if (nonDefaultRows.length === 0) {
          this.isEdit = true;
        }
      }
    } catch {
      // Note: The purpose of this try-catch block is to hide console error messages when switching tabs very quickly
    }

    // Assign a value to validSheet
    this.validSheet = !this.hasInvalidations && this.invalidRows.length === 0 && this.isEdit && !this.isSaved;

    if (!this.isLoading) {
      this.menu.selectedMenuItemCanDeactivate(this.validSheet);
    }

    return this.validSheet;
  }

  /**
   * Set-ups all the validations
   */
  private setValidations() {
    try {
      this.zone.run(() => {
        this.validating = true;

        if (this.sheet && this.spread) {
          suspendAll(this.sheet);
          this.resetInvalidations();
          this.data.forEach((row, rowIndex) => {
            this.removeValidationFromRow(this.sheet, rowIndex);
            this.addValidatorsToRow(rowIndex);
          });
          resumeAll(this.sheet);
          this.isValidSheet();
        }
        this.validating = false;
      });
    } catch (error) {
      // console.warn(`Error setting validations`, { error });
    }
  }

  private addValidatorsToRow(rowIndex: number) {
    this.addNameValidator(rowIndex, COLS_INDEX.name);
    this.addMnemonicValidator(rowIndex, COLS_INDEX.mnemonic);
    // Enables the list dropdown for Type
    this.addTypeValidator(rowIndex, COLS_INDEX.type);
    this.addCalculateGroupValidator(rowIndex, COLS_INDEX.calculate_group);
    this.addDrawOrderValidator(rowIndex, COLS_INDEX.draw_order);
    this.addBalanceSheetValidator(rowIndex, COLS_INDEX.balance_sheet_account_mnemonic);
    this.addBalanceSheetAddFactorValidator(rowIndex, COLS_INDEX.balance_sheet_add_factor);
  }

  private removeValidationFromRow = (sheet: GC.Spread.Sheets.Worksheet, row: number) => {
    sheet.setDataValidator(row, 0, 1, -1, null);
  };

  /**
   * Validation rules for 'name'
   */
  private addNameValidator(rowIndex: number, colIndex: number) {
    if (!this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) && this.data[rowIndex]['mnemonic']) {
      const dv = this.liqSrcService.getNameValidator(rowIndex);
      dv.ignoreBlank(false);
      this.sheet.setDataValidator(rowIndex, colIndex, dv, GC.Spread.Sheets.SheetArea.viewport);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /**
   * Validation rules for 'mnemonic'
   */
  private addMnemonicValidator(rowIndex: number, colIndex: number) {
    if (this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex])) {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    } else {
      const dv = this.liqSrcService.getMnemonicValidator(rowIndex);
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    }
  }

  /**
   * Validation rules for 'type'
   * Accepts only values inside of the list
   */
  private addTypeValidator(rowIndex: number, colIndex: number) {
    // if (!this.sheet.getCell(rowIndex, colIndex).value()) {
    //   this.sheet.getCell(rowIndex, colIndex).value(null);
    // }

    if (
      !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) &&
      this.sheet.getCell(rowIndex, COLS_INDEX['mnemonic']).value() !== null
    ) {
      // && this.data[rowIndex]['mnemonic'] !== null
      const dv = this.liqSrcService.getTypeValidator();
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /**
   * Validation rules for 'calculate_group'
   */
  private addCalculateGroupValidator(rowIndex: number, colIndex: number) {
    if (
      !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) &&
      this.sheet.getCell(rowIndex, COLS_INDEX.mnemonic).value() !== null &&
      this.sheet.getCell(rowIndex, COLS_INDEX.type).value() !== null
    ) {
      // Avoids applying it to blank values
      const dv = this.liqSrcService.getShouldBeNumberValidator();
      dv.ignoreBlank(true);
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /**
   * Validation Rules for 'draw_order'
   */
  private addDrawOrderValidator(rowIndex: number, colIndex: number) {
    if (
      !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) &&
      this.sheet.getCell(rowIndex, COLS_INDEX.mnemonic).value() !== null &&
      this.sheet.getCell(rowIndex, COLS_INDEX.type).value() !== null
    ) {
      // Avoids applying it while the row has not mnemonic or is default row
      const dv = this.liqSrcService.getDrawOrderValidator(rowIndex);
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /**
   * Validation Rules for 'account_mnemonic'
   */
  private addBalanceSheetValidator(rowIndex: number, colIndex: number) {
    if (
      !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) &&
      this.sheet.getCell(rowIndex, COLS_INDEX.mnemonic).value() !== null &&
      this.sheet.getCell(rowIndex, COLS_INDEX.type).value() !== null
    ) {
      // Avoids applying it while the row has not mnemonic or is default row
      const dv = this.liqSrcService.getBalanceSheetAccountListValidator(this.balanceSheetAccounts);
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /**
   * Validation Rules for 'add_factor'
   */
  private addBalanceSheetAddFactorValidator(rowIndex: number, colIndex: number) {
    if (
      !this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(this.data[rowIndex]) &&
      this.sheet.getCell(rowIndex, COLS_INDEX.mnemonic).value() !== null &&
      this.sheet.getCell(rowIndex, COLS_INDEX.type).value() !== null
    ) {
      // Avoids applying it while the row has not mnemonic or is default row
      const dv = this.liqSrcService.getAddFactorValidator(rowIndex);
      this.sheet.setDataValidator(rowIndex, colIndex, dv);
    } else {
      this.sheet.setDataValidator(rowIndex, colIndex, null);
    }
  }

  /*
      ==============================
          Listeners & Initializers
      ==============================
  */

  /*
   *  Other METHODS
   */
  private async loadData() {
    this.isLoading = true;

    return this.getAllLiquiditySources().then(() => {
      this.isLoading = false;
      this.updateSaveButton('first_load'); // Save disabled on first load
    });
  }

  /**
   * Gets all liquidity sources and loads them SpreadSheet
   */
  private async getAllLiquiditySources() {
    const sources = await this.liqSrcService.getAllLiquiditySources();
    this.balanceSheetAccounts = await this.balanceSheetService.getBalanceSheetSavedAccountMnemonics();

    if (sources && typeof sources === 'object' && sources.hasOwnProperty('length') && sources.length > 0) {
      // Delete unnecessary fields
      sources.forEach(src => delete src['user_id']);

      // Assign them
      this.liquiditySources = cloneDeep(sources) as LiquiditySource[];
    } else {
      this.liquiditySources = cloneDeep(BLANK_ROWS_AT_BOTTOM) as LiquiditySource[];
    }

    // Load in the spreadsheet
    try {
      this.loadInSpreadSheet(this.liquiditySources);
    } catch {
      // Note: The purpose of this try-catch block is to hide console error messages when switching tabs very quickly
    }
  }

  /**
   * Loads data into the Spreadsheet
   */
  private loadInSpreadSheet(sources: LiquiditySource[]): void {
    this.zone.run(() => {
      if (sources && JSON.stringify(sources) !== '[]' && sources.length > 0) {
        this.data = cloneDeep(sources);
        this.setValidations();
      }

      this.setDataBinding();
      this.resetMappings();
      this.checkDataLoss();
      this.isEdit = false;

      // Apply protection to all cells in read-only mode (including blank lines)
      if (!this.userRoleCanEdit) {
        this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
        this.sheet.options.isProtected = true;
        this.sheet.options.protectionOptions.allowSelectLockedCells = true;
        SpreadContextMenu.setCopyCutPaste(this.spread);
      }

      try {
        this.sheet.clearSelection();
        this.sheet.setActiveCell(0, 0);
        this.menu.emitResize();
      } catch {
        // Note: The purpose of this try-catch block is to hide console error messages when switching tabs very quickly
      }
    });
  }

  private cleanDefaultOrBlankRows = (data: LiquiditySource[]) => {
    
    const filteredData = data.filter(row => {
      const isEmptyDefault = this.liqSrcService.isEmptyDefaultOrBlankLiquiditySource(row);
      const hasMnemonic = row.mnemonic;
      const hasName = row.name;
      const hasValidSourceSid = row.source_sid >= 0;
      
      return !isEmptyDefault && hasMnemonic && hasName && hasValidSourceSid;
    });
    return filteredData;
  };

  /*
   ** Data-related actions: Save, ...
   */
  public async saveData() {
    this.isSaving = true;
    this.isSaved = false;

    const cleanDataForInsertAndUpdate = this.prepareDataForDatabase();
    
    this.balanceSheetAccounts = await this.balanceSheetService.getBalanceSheetSavedAccountMnemonics();

    // Second round of balance sheet account validations
    if (
      this.balanceSheetAccounts &&
      this.balanceSheetAccounts.length > 0 &&
      cleanDataForInsertAndUpdate.filter(data => Boolean(data.balance_sheet_account_mnemonic)).length > 0 &&
      cleanDataForInsertAndUpdate.some(
        src =>
          src.balance_sheet_account_mnemonic &&
          !this.balanceSheetAccounts.some(acc_mnemonic => acc_mnemonic === src.balance_sheet_account_mnemonic)
      )
    ) {
      this.showFailToast('Balance Sheet Acct invalid. Account Mnemonic must exist in the balance sheet chart of accounts.');
      this.isSaving = false;
      this.loadData().then(() => this.updateSaveButton('save'));
    } else {
      forkJoin([
        this.liqSrcService.performUpdateOperation(cleanDataForInsertAndUpdate, this.dataOriginal),
        this.liqSrcService.performInsertOperation(cleanDataForInsertAndUpdate),
        this.liqSrcService.performDeleteOperation(this.data, this.dataOriginal),
      ])
        .pipe(takeUntil(this.destroyed$), debounceTime(50))
        .toPromise()
        .then(
          ([updated, inserted, deleted]) => {
            // Save confirmation or fail messages
            const success = updated && inserted && deleted && updated.length >= 0 && inserted.length >= 0 && deleted.length >= 0;
            if (success) {
              this.showSuccessToast();
              this.isSaved = true;
            } else {
              this.showFailToast();
              console.warn('Error saving', { updated, inserted, deleted });
            }
          },
          err => {
            this.showFailToast();
            console.error(err);
          }
        )
        .catch(err => {
          this.showFailToast();
          console.error(err);
        })
        .finally(() => {
          this.isSaving = false;
          // Once save ends. Retrieve data from DB and load
          this.loadData().then(() => this.updateSaveButton('save'));
        });
    }
  }

  private prepareDataForDatabase() {
    const cleanedData = this.cleanDefaultOrBlankRows(this.data);
    
    const processedData = cloneDeep(cleanedData).map(row => {
      row.calculate_group = convertBlankTo(row.calculate_group, 0);
      return row;
    });
    
    return processedData;
  }

  // Reset values
  private resetMappings(cleanFlags = true) {
    if (this.sheet) {
      this.data.forEach(row => delete row['user_id']);
      
      this.liqSrcService.addSource_sid(this.data);
      
      this.liqSrcService.generateSidMap(this.data);
      
      this.dataOriginal = cloneDeep(this.data);
      
      if (cleanFlags) {
        this.liqSrcService.setOfSourcesToInsert.clear();
        this.liqSrcService.setOfSourcesToUpdate.clear();
        this.liqSrcService.setOfSourcesToDelete.clear();
      }
      this.addRowsAtTheEnd();
    }
  }

  private addRowsAtTheEnd(qty: number = QTY_EMPTY_BOTTOM_ROWS) {
    const paddingRows = this.sheet.getRowCount() > MINIMUM_ROWS ? qty : MINIMUM_ROWS - this.sheet.getRowCount();
    const rowCount = this.sheet.getRowCount();
    this.zone.run(() => {
      this.sheet.suspendEvent();
      this.sheet.addRows(rowCount, paddingRows);
      this.sheet.resumeEvent();
      this.setValidations();
    });
  }

  // Quick workaround because other attempts failed to open the modal/dialog:
  public goToEquations() {
    const openedDialog = this.dialogRef;
    this.menu.goToMenuItem('liquidity-equations');
    document.getElementById('btn_view_model_data').click();
    // Close previous dialog with some delay
    setTimeout(() => openedDialog.close(), 300);
  }

  /* // ! WARNING
    Removing the async affects seriously the save button enable/disable behavior
    for complex operations. It postpones the execution until the events has ended
  */
  private async updateSaveButton(changeSource: string) {
    const page = 'liquidity-sources';
    this.menu.dialogSaveButton = {
      ...this.menu.dialogSaveButton,
      page,
      enabled: ['save', 'first_load'].includes(changeSource) ? false : await this.saveEnabled(),
      clicked: changeSource === 'save',
      valid:
        changeSource === 'save'
          ? // Forces reset of all the validations
            true
          : this.validSheet,
      source: `${page} ${changeSource || 'reCheck'}`,
    };
  }

  private invalidationsCheck(rowIndex = -1): void {
    const rowInvalidationCheck = (_rowIndex: number) => {
      // Check if some of the fields are invalid for the row with rowIndex _rowIndex
      Object.keys(this.data[_rowIndex]).forEach((colName, col_index) => {
        const validField = this.sheet.isValid(_rowIndex, col_index, this.sheet.getCell(_rowIndex, col_index).value());
        if (!validField) {
          this.hasInvalidations = true;
        }
      });
    };

    if (rowIndex > -1) {
      rowInvalidationCheck(rowIndex);
    } else {
      this.data.forEach((acc, row_index) => rowInvalidationCheck(row_index));
    }

    // Necessary to avoid the save button blocking behavior when the mouse has not move (related: onMouseMove)
    this.menu.emitResize();
  }

  async canDeactivate(): Promise<boolean> {
    return this.menu.canDeactivatePopup();
  }

  private forceReprocessPastedData() {
    // Ensure all pasted rows have proper source_sid assignment
    this.data.forEach((row, index) => {
      if (!row.hasOwnProperty('source_sid') || row.source_sid === null || row.source_sid === undefined) {
        row.source_sid = 0;
      }
    });
    
    // Force a re-parse of the data
    this.data.forEach((row, index) => {
      this.data[index] = this.liqSrcService.parseRowForLiquiditySources(row);
    });
    
    // Update the mappings
    this.resetMappings(false); // Don't clean flags to preserve insert/update/delete tracking
  }
}
